import { useCallback, useEffect, useRef, useState } from "react";

/** How many rows of chips to show before collapsing. */
const LINES = 2;
/** Must match the `gap-1` on the wrapper (0.25rem). */
const GAP = 4;

/**
 * A wrapping list of chips, clamped to two rows with a toggle.
 *
 * A tool like "Adobe Acrobat" belongs to a hundred specialties, and listing
 * them all turned one table row into a screenful. Clamping is done by height
 * rather than by chip count because the chips are all different widths: ten
 * short names fit on two rows where four long ones do not, so any fixed
 * count is wrong somewhere.
 *
 * The row height is read from the first chip rather than hardcoded, so the
 * clamp stays correct if the chip padding or font size ever changes.
 *
 * The toggle only appears when the content actually overflows, so a role
 * with two specialties looks exactly as it does today.
 */
export default function ChipList({ children, count, label = "chips" }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);
  const [overflows, setOverflows] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    /* Never measure while open: the content then fits by definition, which
       would hide the control that closes it again. */
    if (!el || expanded) return;
    const first = el.firstElementChild;
    const rowHeight = first ? first.offsetHeight : 22;
    const limit = rowHeight * LINES + GAP * (LINES - 1);
    setMaxHeight(limit);
    setOverflows(el.scrollHeight - limit > 1);
  }, [expanded]);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    /* The column is fluid, so what fits on two rows changes with the window
       and with the width of neighbouring columns. */
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    /* Keyed on `count`, not `children`: children is a fresh array on every
       render of the table, which would rebuild the observer each time. */
  }, [measure, count]);

  return (
    <div className="flex flex-col items-start gap-1">
      <div
        ref={ref}
        style={expanded || !overflows ? undefined : { maxHeight }}
        className={`flex flex-wrap gap-1 text-sm ${
          expanded || !overflows ? "" : "overflow-hidden"
        }`}>
        {children}
      </div>
      {overflows ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="rounded text-xs font-semibold text-brand-orange underline underline-offset-2 hover:text-brand-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy/40">
          {expanded
            ? "Show less"
            : count
            ? `Show all ${count} ${label}`
            : "Show all"}
        </button>
      ) : null}
    </div>
  );
}
